import React, { useMemo, useRef, useState } from 'react';
import { X, Download, Share2 } from 'lucide-react';
import { toPng } from 'html-to-image';
import toast from 'react-hot-toast';
import { formatMileage } from '../utils/formatters';
import { getCurrentStreak } from '../utils/streakHelpers';

const SharePlanModal = ({
  isOpen,
  onClose,
  weekDays,
  runs,
  selectedPlan,
  theme,
  darkMode,
  getStats,
  types
}) => {
  const cardRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const { weekLabel, plannedMileage, completedMileage, completedRuns, totalRuns, progressPct, streak, daySummaries } = useMemo(() => {
    const primaryWeek = weekDays || [];
    const stats = primaryWeek.length ? getStats(primaryWeek) : { total: 0, planned: 0 };
    const formattedCompleted = formatMileage(stats.total || 0);
    const formattedPlanned = formatMileage(stats.planned || 0);
    const completed = primaryWeek.reduce((acc, day) => {
      const run = runs?.[day.toDateString()];
      if (run && (run.status === 'completed' || run.status === 'adjusted')) {
        return acc + 1;
      }
      return acc;
    }, 0);
    const totals = primaryWeek.reduce((acc, day) => {
      const run = runs?.[day.toDateString()];
      if (run && run.type !== 'cross') {
        return acc + 1;
      }
      return acc;
    }, 0);
    const firstDay = primaryWeek[0];
    const lastDay = primaryWeek[primaryWeek.length - 1];
    const formatDistance = (run) => {
      if (!run) return null;
      const distance = run.status === 'completed' && run.actualDistance
        ? run.actualDistance
        : run.distance;
      if (distance === undefined || distance === null || distance === '') return null;
      const num = parseFloat(distance);
      if (Number.isNaN(num)) return null;
      const rounded = num % 1 === 0 ? num : num.toFixed(1);
      return `${rounded}mi`;
    };

    const weekSummary = primaryWeek.map(day => {
      const run = runs?.[day.toDateString()];
      const dayLabel = day.toLocaleDateString('en-US', { weekday: 'short' });

      if (!run) {
        return {
          dayLabel,
          distanceText: '',
          status: 'off'
        };
      }

      if (run.status === 'rest') {
        return {
          dayLabel,
          distanceText: 'R',
          status: 'rest'
        };
      }

      const distanceText = formatDistance(run) || '—';
      const typeLabel = types?.[run.type]?.name || (run.type ? run.type : 'Run');
      const status = (run.status === 'completed' || run.status === 'adjusted') ? 'completed' : 'planned';

      return {
        dayLabel,
        distanceText,
        status
      };
    });

    const rangeLabel = firstDay
      ? `${firstDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} → ${lastDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : 'This Week';

    return {
      weekLabel: rangeLabel,
      plannedMileage: formattedPlanned,
      completedMileage: formattedCompleted,
      completedRuns: completed,
      totalRuns: totals,
      progressPct: stats.planned ? Math.min(100, Math.round((stats.total / stats.planned) * 100)) : 0,
      streak: getCurrentStreak(runs || {}),
      daySummaries: weekSummary
    };
  }, [weekDays, runs, getStats, types]);

  if (!isOpen) return null;

  const handleSaveImage = async () => {
    if (!cardRef.current) return;
    setIsGenerating(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        backgroundColor: darkMode ? '#020617' : '#f8fafc',
        pixelRatio: 2,
        cacheBust: true
      });
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `scurry-progress-${Date.now()}.png`;
      link.click();
      toast.success('Image saved! Share it with your crew.');
    } catch (error) {
      console.error('Error generating share card', error);
      toast.error('Unable to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share || !cardRef.current) {
      toast.error('Native share not supported on this device yet.');
      return;
    }
    setIsGenerating(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        backgroundColor: darkMode ? '#020617' : '#f8fafc',
        pixelRatio: 2,
        cacheBust: true
      });
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], 'scurry-progress.png', { type: 'image/png' });
      await navigator.share({
        title: 'My Scurry progress',
        text: 'Check out my training week!',
        files: [file]
      });
    } catch (error) {
      console.error('Share failed', error);
      toast.error('Could not share image. Try saving instead.');
    } finally {
      setIsGenerating(false);
    }
  };

  const cardGradient = darkMode
    ? 'bg-gradient-to-br from-slate-900 via-slate-950 to-zinc-900 text-white'
    : 'bg-gradient-to-br from-emerald-100 via-slate-50 to-white text-slate-900';

  const statusIcons = {
    completed: '✅',
    planned: '🗓️',
    rest: '💤',
    off: ''
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className={darkMode ? 'relative w-full max-w-md rounded-3xl bg-slate-900 border border-slate-700 p-6 text-white' : 'relative w-full max-w-md rounded-3xl bg-white border border-slate-200 p-6 text-slate-900'}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-2 right-2 z-10 flex items-center justify-center w-11 h-11 rounded-full hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          onClick={onClose}
          aria-label="Close share modal"
        >
          <X className="w-5 h-5 pointer-events-none" />
        </button>

        <div className="mb-4">
          <p className="text-sm opacity-80">Turn your grind into something shareable.</p>
          <h3 className="text-2xl font-bold">Share your training week</h3>
        </div>

        <div ref={cardRef} className={`${cardGradient} rounded-3xl p-6 shadow-2xl border border-white/10 relative overflow-hidden`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] opacity-80">Scurry</p>
              <h4 className="text-xl font-bold">
                {selectedPlan?.name || 'Custom Plan'}
              </h4>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${darkMode ? 'bg-white/10 text-white' : 'bg-white text-emerald-600 border border-emerald-100'}`}>
              {weekLabel}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="bg-white/10 rounded-2xl p-3">
              <p className="text-[10px] tracking-wide uppercase opacity-70">Miles</p>
              <p className="text-2xl font-bold mt-1">{completedMileage}</p>
              <p className="text-[11px] opacity-70">of {plannedMileage}</p>
            </div>
            <div className="bg-white/10 rounded-2xl p-3">
              <p className="text-[10px] tracking-wide uppercase opacity-70">Runs</p>
              <p className="text-2xl font-bold mt-1">{completedRuns}/{totalRuns}</p>
              <p className="text-[11px] opacity-70">{progressPct}% goal</p>
            </div>
            <div className="bg-white/10 rounded-2xl p-3">
              <p className="text-[10px] tracking-wide uppercase opacity-70">Streak</p>
              <p className="text-2xl font-bold mt-1">{streak}d</p>
              <p className="text-[11px] opacity-70">Keep going 🌱</p>
            </div>
          </div>

          <div className="bg-white/5 rounded-2xl p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wide opacity-70">Week at a glance</p>
              <span className="text-xs opacity-60">Daily miles</span>
            </div>
            <div className="grid grid-cols-7 gap-2">
              {daySummaries.length === 0 && (
                <div className="col-span-7 text-center text-sm opacity-70 py-2">
                  No runs scheduled this week yet.
                </div>
              )}
              {daySummaries.map((day) => (
                <div
                  key={day.dayLabel}
                  className="rounded-xl bg-white/10 text-center px-2 py-2 flex flex-col items-center gap-1"
                >
                  <p className="text-[10px] uppercase opacity-70">{day.dayLabel}</p>
                  <p className="text-lg font-semibold leading-tight whitespace-nowrap">
                    {day.distanceText || '\u00A0'}
                  </p>
                  <p className="text-base">{statusIcons[day.status] || ''}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3">
          <button
            onClick={handleSaveImage}
            disabled={isGenerating}
            className={`w-full flex items-center justify-center gap-2 rounded-2xl py-3 font-semibold text-white ${theme.primary} ${theme.hover} disabled:opacity-50`}
          >
            <Download className="w-4 h-4" />
            {isGenerating ? 'Generating...' : 'Save Image'}
          </button>

          <button
            onClick={handleNativeShare}
            disabled={isGenerating}
            className={darkMode ? 'w-full flex items-center justify-center gap-2 rounded-2xl py-3 font-semibold bg-slate-800 border border-slate-700' : 'w-full flex items-center justify-center gap-2 rounded-2xl py-3 font-semibold bg-slate-100 border border-slate-200'}
          >
            <Share2 className="w-4 h-4" />
            Share with Friends
          </button>

          <p className="text-xs text-center opacity-70">
            Works great on Instagram Stories, Strava posts, or texts to your running buddies.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharePlanModal;
